import {
  Box,
  Button,
  keyframes,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import AutorenewTwoToneIcon from "@mui/icons-material/AutorenewTwoTone";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ValidationModal from "./ValidationModal";
import { useState } from "react";

const statusConfig = {
  validating: {
    icon: <AutorenewTwoToneIcon sx={{ color: "primary.main" }} />,
    bg: "#F7F6FF",
    color: "primary.main",
    title: "Validating file...",
  },
  success: {
    icon: <CheckCircleIcon sx={{ color: "success.main" }} />,
    bg: "#EDF7ED",
    color: "success.main",
    title: "Validation Successful",
  },
  error: {
    icon: <ErrorIcon sx={{ color: "error.main" }} />,
    bg: "#FDEDED",
    color: "#5F2120",
    title: "Validation Failed",
  },
  warning: {
    icon: <WarningIcon sx={{ color: "warning.main" }} />,
    bg: "#FFF4E5",
    color: "#663C00",
    title: "Validation Warning",
  },
};

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ValidationStatus = ({
  status = "",
  validationStatusData = {},
  uploadAction = "",
  selectedWarningType = "",
  // onWarningSelect = () => {},
}) => {
  const [showValidationModal, setShowValidationModal] = useState(false);

  const config = statusConfig[status] || {};
  if (!status) {
    return null;
  }

  let {
    currentRows,
    uploadedRows,
    delta,
    warningAndErrorsData = [],
  } = validationStatusData;
  const visibleItems = warningAndErrorsData.slice(0, 3);

  switch (status) {
    case "validating":
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: config.bg,
            color: config.color,
            width: "100%",
          }}
        >
          {/* Left: Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: "2px",
              animation: `${spin} 1s linear infinite`,
            }}
          >
            {config.icon}
          </Box>

          {/* Right: Title + Details */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={config.color}
            >
              {config.title}
            </Typography>
          </Box>
        </Box>
      );
    case "success":
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: config.bg,
            color: config.color,
            width: "100%",
          }}
        >
          {/* Left: Icon */}
          <Box sx={{ display: "flex", alignItems: "center", mt: "2px" }}>
            {config.icon}
          </Box>

          {/* Right: Title + Details */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={config.color}
              sx={{ mb: 1 }}
            >
              {config.title}
            </Typography>

            {/* Row Info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pl: 1,
              }}
            >
              {uploadAction === "OVER_WRITE" && (
                <Typography variant="body2" color="text.primary">
                  Current no. of rows: <b>{currentRows}</b>
                </Typography>
              )}
              <Typography variant="body2" color="text.primary">
                Uploaded no. of rows: <b>{uploadedRows}</b>
              </Typography>
              {uploadAction === "OVER_WRITE" && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <FiberManualRecordIcon
                    sx={{ fontSize: 14, color: config.color }}
                  />{" "}
                  Delta: <b>{delta}</b>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      );
    case "warning":
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: config.bg,
            color: config.color,
            width: "100%",
          }}
        >
          {/* Left: Icon */}
          <Box sx={{ display: "flex", alignItems: "center", mt: "2px" }}>
            {config.icon}
          </Box>

          {/* Right: Title + Details */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={config.color}
              sx={{ mb: 1 }}
            >
              {config.title}
            </Typography>

            {/* Row Info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pl: 1,
              }}
            >
              {uploadAction === "OVER_WRITE" && (
                <Typography variant="body2" color="text.primary">
                  Current no. of rows: <b>{currentRows}</b>
                </Typography>
              )}
              <Typography variant="body2" color="text.primary">
                Uploaded no. of rows: <b>{uploadedRows}</b>
              </Typography>
              {uploadAction === "OVER_WRITE" && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <FiberManualRecordIcon
                    sx={{ fontSize: 14, color: config.color }}
                  />{" "}
                  Delta: <b>{delta}</b>
                </Typography>
              )}
            </Box>
            <Box sx={{ pl: 1 }}>
              <Typography
                mt={2}
                variant="body2"
                color={config.color}
                fontWeight={600}
              >
                Warning ({warningAndErrorsData.length})
              </Typography>
              <List sx={{ listStyleType: "disc", pl: 2 }}>
                {visibleItems.map((item, index) => (
                  <ListItem
                    key={`item-${index}`}
                    sx={{ display: "list-item", p: 0 }}
                  >
                    <ListItemText
                      primary={`Row ${item.lineNumber}: ${item?.messages?.join(", ")}`}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>

              {warningAndErrorsData.length > 3 && (
                <>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowValidationModal(true)}
                  >
                    Show more
                  </Button>
                  <ValidationModal
                    key={new Date()}
                    open={showValidationModal}
                    onClose={() => {
                      setShowValidationModal(false);
                    }}
                    bgcolor={config.bg}
                    color={config.color}
                    warningAndErrorsData={warningAndErrorsData.slice(0, 150)}
                  />
                </>
              )}
            </Box>
            <Typography
              my={1}
              variant="body2"
              color={config.color}
              fontWeight={600}
            >
              We will consume valid data only, all invalid rows will be skipped.
            </Typography>
          </Box>
        </Box>
      );
    case "error":
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: config.bg,
            color: config.color,
            width: "100%",
          }}
        >
          {/* Left: Icon */}
          <Box sx={{ display: "flex", alignItems: "center", mt: "2px" }}>
            {config.icon}
          </Box>

          {/* Right: Title + Details */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={config.color}
            >
              {config.title}
            </Typography>

            {/* Row Info */}
            <Box sx={{ pl: 1 }}>
              <List sx={{ listStyleType: "disc", pl: 2 }}>
                {visibleItems.map((item, index) => (
                  <ListItem
                    key={`item-${index}`}
                    sx={{ display: "list-item", p: 0 }}
                  >
                    <ListItemText
                      primary={`Row ${item.lineNumber}: ${item?.messages?.join(", ")}`}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>

              {warningAndErrorsData.length > 3 && (
                <>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowValidationModal(true)}
                  >
                    Show more
                  </Button>
                  <ValidationModal
                    key={new Date()}
                    open={showValidationModal}
                    onClose={() => {
                      setShowValidationModal(false);
                    }}
                    bgcolor={config.bg}
                    color={config.color}
                    warningAndErrorsData={warningAndErrorsData}
                  />
                </>
              )}
            </Box>

            <Typography mt={2} variant="body2" color={config.color}>
              Please make changes re-upload the file again
            </Typography>
          </Box>
        </Box>
      );
    default:
      return null;
  }
};

export default ValidationStatus;
